import { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, RefreshCw, FolderKanban, Info } from 'lucide-react';
import ProjectCard from './ProjectCard';
import JobDetail from './JobDetail';
import SocialCredentialsInfo from './SocialCredentialsInfo';
import BibleWizard from './BibleWizard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProjectsPanel = ({ open, onClose }) => {
  const [projects, setProjects] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [showSocial, setShowSocial] = useState(false);
  const [bibleWizard, setBibleWizard] = useState(null);   // { slug, name } | null

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, jRes] = await Promise.all([
        fetch(`${API}/projects`, { credentials: 'include' }),
        fetch(`${API}/jobs?limit=100`, { credentials: 'include' }),
      ]);
      if (pRes.ok) {
        const pd = await pRes.json();
        setProjects(pd.projects || []);
      }
      if (jRes.ok) {
        const jd = await jRes.json();
        setJobs(jd.jobs || []);
      }
    } catch (e) {
      console.error('Error loading projects:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  // Auto-refresh while there are pending/running jobs
  useEffect(() => {
    if (!open) return;
    const hasActive = jobs.some(j => j.status === 'pending' || j.status === 'running');
    if (!hasActive) return;
    const interval = setInterval(load, 4000);
    return () => clearInterval(interval);
  }, [open, jobs, load]);

  const jobsByProject = jobs.reduce((acc, j) => {
    (acc[j.project_slug] = acc[j.project_slug] || []).push(j);
    return acc;
  }, {});

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
            onClick={onClose}
            data-testid="projects-panel-backdrop"
          />

          {/* Side Drawer */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            className="fixed top-0 right-0 h-full w-full sm:w-[520px] bg-[#0A0A0A] border-l border-neon-red/30 z-50 flex flex-col shadow-[0_0_60px_rgba(255,0,51,0.2)]"
            data-testid="projects-panel"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-neon-red/20 bg-black/40 backdrop-blur-xl">
              <div className="flex items-center gap-3">
                <FolderKanban className="w-5 h-5 text-neon-red" />
                <div>
                  <h2 className="font-outfit text-xl font-bold text-white">Proyectos</h2>
                  <p className="font-jetbrains text-[10px] text-white/50 tracking-widest uppercase">
                    Agentes especializados de Alivan
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowSocial(v => !v)}
                  data-testid="show-social-info"
                  className="p-2 text-white/60 hover:text-neon-red transition-colors"
                  title="Estado de credenciales de redes"
                >
                  <Info className="w-4 h-4" />
                </button>
                <button
                  onClick={load}
                  data-testid="refresh-projects"
                  className="p-2 text-white/60 hover:text-white transition-colors"
                  title="Actualizar"
                >
                  <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                </button>
                <button
                  onClick={onClose}
                  data-testid="close-projects-panel"
                  className="p-2 text-white/60 hover:text-white transition-colors"
                  title="Cerrar"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {showSocial && <SocialCredentialsInfo onClose={() => setShowSocial(false)} />}

              {selectedJobId ? (
                <JobDetail
                  jobId={selectedJobId}
                  onBack={() => setSelectedJobId(null)}
                  onRefresh={load}
                />
              ) : (
                <>
                  {projects.length === 0 && !loading && (
                    <p className="font-jetbrains text-sm text-white/60 text-center py-8">
                      Sin proyectos aún.
                    </p>
                  )}
                  {projects.map(p => (
                    <ProjectCard
                      key={p.project_id}
                      project={p}
                      jobs={jobsByProject[p.project_slug] || []}
                      onOpenJob={(jobId) => setSelectedJobId(jobId)}
                      onOpenBible={(slug, name) => setBibleWizard({ slug, name })}
                    />
                  ))}
                </>
              )}
            </div>
          </motion.div>

          <BibleWizard
            open={!!bibleWizard}
            onClose={() => setBibleWizard(null)}
            projectSlug={bibleWizard?.slug}
            projectName={bibleWizard?.name}
          />
        </>
      )}
    </AnimatePresence>
  );
};

export default ProjectsPanel;
